export type TenantStore = {
    id: string;
    name: string;
    code: string;
    storeType: string;
    isDefault: boolean;
};

export type Tenant = {
    id: string;
    name: string;
    isDefault: boolean;
    tenantStores: TenantStore[];
};

type TenantStoreApiItem = Partial<TenantStore>;

type TenantApiItem = Partial<Omit<Tenant, 'tenantStores'>> & {
    tenantStores?: TenantStoreApiItem[];
};

type TenantsApiResponse = {
    tenants?: TenantApiItem[];
};

const hasStoreId = (store: TenantStoreApiItem): store is TenantStoreApiItem & { id: string } =>
    typeof store.id === 'string';

const transformTenantStore = (store: TenantStoreApiItem & { id: string }): TenantStore => ({
    id: store.id,
    name: store.name ?? '',
    code: store.code ?? '',
    storeType: store.storeType ?? '',
    isDefault: store.isDefault ?? false,
});

const hasId = (tenant: TenantApiItem): tenant is TenantApiItem & { id: string } =>
    typeof tenant.id === 'string';

const transformTenant = (tenant: TenantApiItem & { id: string }): Tenant => ({
    id: tenant.id,
    name: tenant.name ?? '',
    isDefault: tenant.isDefault ?? false,
    tenantStores: Array.isArray(tenant.tenantStores)
        ? tenant.tenantStores.filter(hasStoreId).map(transformTenantStore)
        : [],
});

/**
 * Tenants with their embedded stores. Used only to resolve the default
 * tenant's default stores when creating a new instance.
 */
export const tenantsQuery = {
    query: () => ({
        url: '/mp/1/tenants',
        method: 'get',
    }),
    transformResponse: (response: TenantsApiResponse): Tenant[] =>
        (response?.tenants ?? []).filter(hasId).map(transformTenant),
};

export type DefaultStoreIds = {
    imageStoreId: string;
    clientStoreId: string;
};

/**
 * Resolves the default tenant's default image and client store ids — the
 * stores the legacy admin app attaches to every newly created subscription.
 * Returns null when no default tenant or default store exists.
 */
export const findDefaultStoreIds = (tenants: Tenant[]): DefaultStoreIds | null => {
    const defaultTenant = tenants.find((tenant) => tenant.isDefault);
    if (!defaultTenant) return null;

    const imageStore = defaultTenant.tenantStores.find(
        (store) => store.storeType === 'image' && store.isDefault,
    );
    const clientStore = defaultTenant.tenantStores.find(
        (store) => store.storeType === 'client' && store.isDefault,
    );
    if (!imageStore || !clientStore) return null;

    return { imageStoreId: imageStore.id, clientStoreId: clientStore.id };
};
